import React, { useState, useCallback, useEffect } from 'react'
import {
  Button,
  Container,
  Grid,
  Select,
  MenuItem,
  Checkbox,
  TableContainer,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Table,
  Paper,
  TableSortLabel,
  Tooltip
} from '@mui/material'
import { AdapterDateFns as AdapterDateFnsV3 } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import LoadingSpinner from '../../../Shared/LoadingSpinner/LoadingSpinner'
import './Notifications.scss'
import { format, isValid, subDays } from 'date-fns'
import { useCMSContext } from '../../CMSContext'
import NotificationsActions from './NotificationsActions'
import Interceptor from '../../../../Services/serviceBase.interceptor'
import { useErrorSnackBarContext } from '../../../Shared/ErrorSnackBar/ErrorSnackBarContext'
import { useMenuSectionContext } from './../../MenuSection/MenuSectionContext'
import { IARDialog } from '../../../Shared/Dialog/IARDialog'
import NotificationTypes from '../../../../Constants/notificationTypes'
import { parseDate } from '../../../../../resources/Utils'

function Notifications() {
  const { errorSnackBarActions } = useErrorSnackBarContext()
  const menuSectionContext = useMenuSectionContext()
  const { setLastCustomSectionId } = menuSectionContext.actions
  const cmsContext = useCMSContext()
  const { fireDepartment, iarMember, isReadOnlyMember } = cmsContext.state
  const [notificationsEntity, setNotificationsEntity] = useState({ data: [] })
  const [deleteDialog, setDeleteDialog] = useState({ open: false })
  const [allSelected, setAllSelected] = useState(false)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [notificationType, setNotificationType] = useState(NotificationTypes.ALL)
  const [posting, setPosting] = useState(false)
  const orderByDefaultProperty = 'body'
  const [hideDeleted, setHideDeleted] = useState(false)
  const [order, setOrder] = useState({ direction: '', property: '' })
  const DATE_FORMAT_PATTERN = 'yyyy-MM-dd'

  Interceptor.interceptor(errorSnackBarActions)

  async function getNotifications() {
    const payload = {
      notificationType,
      startDate: format(startDate, DATE_FORMAT_PATTERN),
      endDate: format(endDate, DATE_FORMAT_PATTERN),
      identifier: fireDepartment.identifier
    }
    const response = await NotificationsActions.getNotifications(JSON.stringify(payload))
    if (response && response.data) {
      const formatedData =
        response.data &&
        response.data.map((resNotification) => {
          if (!resNotification.notificationTypeName) {
            resNotification['notificationTypeName'] = ''
          }
          if (!resNotification.deleteBy) {
            resNotification['deleteBy'] = ''
          }
          if (!resNotification.deleteLocalDate) {
            resNotification['deleteLocalDate'] = ''
          }
          return resNotification
        })
      setNotificationsEntity({ data: formatedData })
    }
    setHideDeleted(false)
    setPosting(false)
  }

  useEffect(() => {
    setLastCustomSectionId('')
    if (fireDepartment !== null && fireDepartment !== undefined) {
      setupDates()
    }
  }, [fireDepartment, iarMember])

  function setupDates() {
    const startD = subDays(new Date(), 30)
    setStartDate(startD)
    setEndDate(new Date())
  }

  const handleRowClick = useCallback(
    (event, id) => {
      var selectedNotification = notificationsEntity.data.find((el) => el._id === id)
      var notificationsList = notificationsEntity.data
      var indexOfSelected = notificationsList.findIndex((el) => el._id === id)
      selectedNotification.isSelected = !selectedNotification.isSelected
      notificationsList[indexOfSelected] = selectedNotification
      setNotificationsEntity({
        ...notificationsEntity,
        notificationsEntity: notificationsList
      })
    },
    [notificationsEntity]
  )

  const onSelectAllClick = useCallback(() => {
    var notificationsList = notificationsEntity.data
    notificationsList
      .filter((elem) => elem.isDeleted === false)
      .map((el) => {
        el.isSelected = !allSelected
      })
    setNotificationsEntity({
      ...notificationsEntity,
      notificationsEntity: notificationsList
    })
    setAllSelected(!allSelected)
  }, [notificationsEntity, allSelected])

  const openConfirmationDialog = () => {
    setDeleteDialog({ open: true })
  }

  const deleteRecord = useCallback(() => {
    const fullUserName = iarMember.fullName
    async function deleteSelectedNotifications() {
      var ids = notificationsEntity.data
        .map((el) => {
          if (el.isSelected) {
            return el._id
          } else {
            return null
          }
        })
        .filter((el) => el !== null)
      const areRecordsDeleted = await NotificationsActions.deleteNotifications(ids, fullUserName)
      if (areRecordsDeleted)
        notificationsEntity.data = notificationsEntity.data.filter(
          (el) => el.isSelected === undefined
        )

      setDeleteDialog({ open: false })
    }
    deleteSelectedNotifications()
  }, [notificationsEntity, iarMember])

  const cancelDialog = () => {
    setDeleteDialog({ open: false })
  }

  const getDatesBetweenDates = (startDate, endDate) => {
    let dates = []
    const theDate = new Date(startDate)
    while (theDate < endDate) {
      dates = [...dates, new Date(theDate)]
      theDate.setDate(theDate.getDate() + 1)
    }
    return dates.length
  }

  const search = () => {
    if (isUserInputsValid()) {
      const dateRange = getDatesBetweenDates(startDate, endDate)
      if (startDate.getTime() > endDate.getTime()) {
        showErrorSnack('Start Date cannot be greater than End Date')
        return
      }
      setPosting(true)
      if (dateRange > 30) {
        showErrorSnack('Please provide a search no longer than 30 days')
        setPosting(false)
        return
      }
      getNotifications()
    }
  }

  const isUserInputsValid = () => {
    if (!isValid(startDate)) {
      showErrorSnack('Provide valid value for start date')
      return false
    }
    if (!isValid(endDate)) {
      showErrorSnack('Provide valid value for end date')
      return false
    }

    return true
  }

  const showErrorSnack = (errorMessage) => {
    errorSnackBarActions.setOpen(true, errorMessage, 'warning')
  }

  const showDeleted = () => {
    setHideDeleted(!hideDeleted)
  }

  const handleStartDateChange = (date) => setStartDate(date)
  const handleEndDateChange = (date) => setEndDate(date)
  const handleTypeSelection = (e) => {
    setNotificationType(Array.isArray(e.target.value) ? e.target.value : [e.target.value])
  }

  const handleRequestSort = (event, value) => {
    const isAsc = order.property === value && order.direction === 'desc'
    setOrder({ direction: isAsc ? 'asc' : 'desc', property: value })
  }

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property)
  }

  useEffect(() => {
    handleRequestSort(null, orderByDefaultProperty)
  }, [])

  const compare = (a, b) => {
    if (order.direction === 'asc') {
      return Date.parse(a[order.property]) && Date.parse(b[order.property])
        ? Date.parse(a[order.property]) - Date.parse(b[order.property])
        : a[order.property].localeCompare(b[order.property])
    } else {
      return Date.parse(a[order.property]) && Date.parse(b[order.property])
        ? Date.parse(b[order.property]) - Date.parse(a[order.property])
        : b[order.property].localeCompare(a[order.property])
    }
  }

  const renderTable = () => {
    if (notificationsEntity.data && notificationsEntity.data.length > 0) {
      if (hideDeleted) {
        return notificationsEntity.data.map((row) => (
          <TableRow
            key={row._id}
            hover
            selected={row.isSelected}
            onClick={
              row.isDeleted === true
                ? (event) => event.preventDefault()
                : (event) => handleRowClick(event, row._id)
            }
          >
            <TableCell className="notifications-row" component="th" scope="row">
              <Tooltip title={row.body} placement="right">
                <div>{row.body}</div>
              </Tooltip>
            </TableCell>
            <TableCell className="notifications-row" component="th" scope="row">
              {row.agencyName}
            </TableCell>
            <TableCell component="th" scope="row">
              {row.userName}
            </TableCell>
            <TableCell align="center" component="th" scope="row">
              {row.notificationTypeName}
            </TableCell>
            <TableCell align="center">
              {parseDate(row.localDate || row.date, 'HH:mm MMM dd, y')}
            </TableCell>
            <TableCell component="th" scope="row">
              {row.deleteBy && row.deleteBy}
            </TableCell>
            <TableCell align="center">
              {parseDate(row.deleteLocalDate || row.deleteDate, 'HH:mm MMM dd, y')}
            </TableCell>
          </TableRow>
        ))
      } else {
        return notificationsEntity.data
          .filter((elem) => elem.isDeleted === false)
          .map((row) => (
            <TableRow
              key={row._id}
              hover
              selected={row.isSelected}
              onClick={
                row.isDeleted === true
                  ? (event) => event.preventDefault()
                  : (event) => handleRowClick(event, row._id)
              }
            >
              <TableCell className="notifications-row" component="th" scope="row">
                <Tooltip title={row.body} placement="right">
                  <div>{row.body}</div>
                </Tooltip>
              </TableCell>
              <TableCell className="notifications-row" component="th" scope="row">
                {row.agencyName}
              </TableCell>
              <TableCell component="th" scope="row">
                {row.userName}
              </TableCell>
              <TableCell align="center" component="th" scope="row">
                {row.notificationTypeName}
              </TableCell>
              <TableCell align="center">
                {parseDate(row.localDate || row.date, 'HH:mm MMM dd, y')}
              </TableCell>
            </TableRow>
          ))
      }
    }
  }

  useEffect(() => {
    if (order && order.property && order.direction) {
      if (notificationsEntity.data && notificationsEntity.data.length) {
        const sortedData = notificationsEntity.data.sort(compare)
        setNotificationsEntity({
          ...notificationsEntity,
          notificationsEntity: sortedData
        })
      }
    }
  }, [order])

  return (
    <div className="notifications">
      <div className="page-header">
        <span id="notificationsTitle">Notifications History</span>
      </div>
      <IARDialog
        open={deleteDialog.open}
        title="Confirm"
        content={
          <div className="dialogContent">
            <div>Do you want to delete the selected records?</div>
          </div>
        }
        onOk={deleteRecord}
        onCancel={cancelDialog}
      />
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid size={12}>
            <div className="page-description">
              Here you can view a history of notifications and alerts that have been sent to your
              community through your MyLS app.
            </div>
          </Grid>
          <Grid size={12}>
            <Grid container className="controls-container" spacing={2}>
              <Grid size={{ xs: 12, sm: 6, lg: 2 }} className="item">
                <label htmlFor="status">Notification Types</label>
                <Select
                  id="notificationTypes"
                  variant="outlined"
                  defaultValue={NotificationTypes.ALL}
                  className="notification-types-field"
                  onChange={handleTypeSelection}
                >
                  <MenuItem value={NotificationTypes.ALL}>ALL</MenuItem>
                  <MenuItem value={NotificationTypes.NEWS}>NEWS</MenuItem>
                  <MenuItem value={NotificationTypes.CUSTOM}>CUSTOM</MenuItem>
                  <MenuItem value={NotificationTypes.ALERT}>ALERT</MenuItem>
                </Select>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 2 }} className="item">
                <label htmlFor="startDate">Start Date</label>
                <br />
                <LocalizationProvider dateAdapter={AdapterDateFnsV3}>
                  <DatePicker
                    id="startDate"
                    className="date-field"
                    disableFuture
                    format={DATE_FORMAT_PATTERN}
                    value={startDate}
                    onChange={handleStartDateChange}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 2 }} className="item">
                <label htmlFor="endDate">End Date</label>
                <br />
                <LocalizationProvider dateAdapter={AdapterDateFnsV3}>
                  <DatePicker
                    id="endDate"
                    className="date-field"
                    disableFuture
                    format={DATE_FORMAT_PATTERN}
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </LocalizationProvider>
              </Grid>

              {notificationsEntity.data && notificationsEntity.data.length > 0 && (
                <Grid size={{ xs: 12, sm: 6, lg: 2 }} className="item">
                  <Button
                    variant="contained"
                    id="showDeleted"
                    size="medium"
                    className="showDeleted"
                    onClick={showDeleted}
                  >
                    {hideDeleted ? 'Hide' : 'Show'} deleted
                  </Button>
                </Grid>
              )}

              {notificationsEntity.data &&
                notificationsEntity.data.length > 0 &&
                notificationsEntity.data.findIndex((el) => el.isSelected) > -1 && (
                  <Grid size={{ xs: 12, sm: 6, lg: 2 }} className="item">
                    <Button
                      style={{ right: 0 }}
                      variant="contained"
                      id="deleteNotificationButton"
                      size="medium"
                      className="deleteNotificationButton"
                      onClick={openConfirmationDialog}
                      disabled={isReadOnlyMember}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}

              <Grid size={{ xs: 12, sm: 6, lg: 2 }} offset={{ lg: 'auto' }} className="item">
                <Button
                  color="primary"
                  size="medium"
                  variant="contained"
                  className="searchNotificationsButton"
                  disabled={posting}
                  onClick={search}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} className="table-container">
            {posting ? (
              <LoadingSpinner />
            ) : (
              <TableContainer component={Paper} className="notification-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        key="body"
                        align="left"
                        padding="normal"
                        sortDirection={
                          order && !order.property === '' && order.property === 'body'
                            ? order.property
                            : false
                        }
                      >
                        <Checkbox
                          onChange={onSelectAllClick}
                          inputProps={{ 'arial-label': '' }}
                          checked={allSelected}
                        />
                        <TableSortLabel
                          active={order && order.property === 'body'}
                          direction={order && order.property === 'body' ? order.direction : 'desc'}
                          onClick={createSortHandler('body')}
                        >
                          Notification
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        key="agencyName"
                        align="center"
                        padding="normal"
                        sortDirection={
                          order && !order.property === '' && order.property === 'agencyName'
                            ? order.property
                            : false
                        }
                      >
                        <TableSortLabel
                          active={order && order.property === 'agencyName'}
                          direction={
                            order && order.property === 'agencyName' ? order.direction : 'desc'
                          }
                          onClick={createSortHandler('agencyName')}
                        >
                          Entity's Name
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        key="userName"
                        align="center"
                        padding="normal"
                        sortDirection={
                          order && !order.property === '' && order.property === 'userName'
                            ? order.property
                            : false
                        }
                      >
                        <TableSortLabel
                          active={order && order.property === 'userName'}
                          direction={
                            order && order.property === 'userName' ? order.direction : 'desc'
                          }
                          onClick={createSortHandler('userName')}
                        >
                          Logged By
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        key="notificationTypeName"
                        align="center"
                        padding="normal"
                        sortDirection={
                          order &&
                          !order.property === '' &&
                          order.property === 'notificationTypeName'
                            ? order.property
                            : false
                        }
                      >
                        <TableSortLabel
                          active={order && order.property === 'notificationTypeName'}
                          direction={
                            order && order.property === 'notificationTypeName'
                              ? order.direction
                              : 'desc'
                          }
                          onClick={createSortHandler('notificationTypeName')}
                        >
                          Notification Type
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        key="localDate"
                        align="center"
                        padding="normal"
                        sortDirection={
                          order && !order.property === '' && order.property === 'localDate'
                            ? order.property
                            : false
                        }
                      >
                        <TableSortLabel
                          active={order && order.property === 'localDate'}
                          direction={
                            order && order.property === 'localDate' ? order.direction : 'desc'
                          }
                          onClick={createSortHandler('localDate')}
                        >
                          Date Sent
                        </TableSortLabel>
                      </TableCell>

                      {hideDeleted ? (
                        <TableCell
                          key="deleteBy"
                          align="center"
                          padding="normal"
                          sortDirection={
                            order && !order.property === '' && order.property === 'deleteBy'
                              ? order.property
                              : false
                          }
                        >
                          <TableSortLabel
                            active={order && order.property === 'deleteBy'}
                            direction={
                              order && order.property === 'deleteBy' ? order.direction : 'desc'
                            }
                            onClick={createSortHandler('deleteBy')}
                          >
                            Deleted By
                          </TableSortLabel>
                        </TableCell>
                      ) : (
                        <></>
                      )}

                      {hideDeleted ? (
                        <TableCell
                          key="deleteLocalDate"
                          align="center"
                          padding="normal"
                          sortDirection={
                            order && !order.property === '' && order.property === 'deleteLocalDate'
                              ? order.property
                              : false
                          }
                        >
                          <TableSortLabel
                            active={order && order.property === 'deleteLocalDate'}
                            direction={
                              order && order.property === 'deleteLocalDate'
                                ? order.direction
                                : 'desc'
                            }
                            onClick={createSortHandler('deleteLocalDate')}
                          >
                            Deleted Date
                          </TableSortLabel>
                        </TableCell>
                      ) : (
                        <></>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>{renderTable()}</TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default Notifications
